import * as React from "react";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartCategoryAxisLabels,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  SharedTooltipContext,
  TooltipContext,
} from "@progress/kendo-react-charts";
import s from "./MultiLineChart.module.scss";
import { Empty } from "@iliotech/widgets";
import { lineChartTooltipRenderer } from "../TooltipRenderer/TooltipRenderer";
import groupby from "lodash.groupby";
import { IChartDataItemV2 } from "@iliotech/types";
import { ToggableChartLegend } from "../ToggableChartLegend/ToggableChartLegend";
import { useToggableLegendV2 } from "@iliotech/data-wire";
import moment from "moment";

interface IProps {
  data: IChartDataItemV2[];
  valueFormatter: (val: any) => string;
  lineType?: "line" | "area";
  tooltipProps?: {
    hideTotal?: boolean;
    renderer?(context: TooltipContext | SharedTooltipContext): React.ReactNode;
  };
  maxDivisions?: number;
  withAnimations?: boolean;
  generating?: boolean;
  height?: number;
  width?: number;
  legendProps?: {
    hideLegend?: boolean;
    hideTotal?: boolean;
    categoryTotalOverride?:
      | ((data: IChartDataItemV2[], category: string) => number)
      | undefined;
  };
}

export const MultiLineChart = ({
  data,
  valueFormatter,
  lineType = "line",
  withAnimations = false,
  legendProps,
  tooltipProps,
  maxDivisions,
  generating,
  height,
  width,
}: IProps) => {
  const { legend, onLegendItemClick, toggledData } = useToggableLegendV2({
    valueFormatter,
    chartData: data,
    toggableTotal: false,
    categoryTotalOverride: legendProps?.categoryTotalOverride,
  });

  const toggledDataSorted = toggledData.sort(
    (a, b) =>
      (
        (typeof a.date === "string" ? moment(a.date) : a.date) as moment.Moment
      )?.diff(b.date)!
  );

  const sumArray = (array: IChartDataItemV2[]) =>
    array.reduce((prev, curr) => ({
      category: prev.category,
      name: prev.name,
      id: prev.id,
      value: prev.value + curr.value,
      color: prev.color,
    }));

  const groupedDataByCategory: IChartDataItemV2[][] = React.useMemo(
    () =>
      Object.values(
        groupby(toggledDataSorted, (item: IChartDataItemV2) => item.category)
      ),
    [toggledDataSorted]
  );

  const minValue = React.useMemo(() => {
    const dataWithGroupedDates: any = [];
    Object.values(groupby(toggledDataSorted, (item) => item.id)).forEach(
      (group) => {
        Object.values(groupby(group, "name")).forEach((group) => {
          dataWithGroupedDates.push(sumArray(group));
        });
      }
    );

    const minimumValue = (
      dataWithGroupedDates ? [...dataWithGroupedDates] : []
    ).sort((a, b) => a.value - b.value)?.[0]?.value;
    return minimumValue > 0 ? 0 : minimumValue;
  }, [toggledDataSorted]);

  if (!data?.length) {
    return <Empty />;
  }
  return (
    <div>
      <Chart
        className={s.chartContainer}
        transitions={withAnimations}
        style={{ height: height, width: width }}
      >
        <ChartArea height={height} width={width} />
        <ChartTooltip
          opacity={0.9}
          background={"var(--dark)"}
          shared={true}
          render={
            tooltipProps?.renderer ||
            ((context) =>
              lineChartTooltipRenderer(
                context,
                valueFormatter,
                toggledDataSorted,
                lineType === "area",
                tooltipProps?.hideTotal
              ))
          }
        />
        <ChartArea background={"transparent"} />

        <ChartSeries>
          {groupedDataByCategory.map((serie, i) => (
            <ChartSeriesItem
              key={i}
              type={lineType}
              markers={{
                visible: false,
              }}
              color={serie?.[0]?.color}
              negativeValues={{ visible: true, color: "magenta" }}
              data={serie}
              width={2}
              categoryField="date"
              field={"value"}
              maxSize={1}
            />
          ))}
        </ChartSeries>
        <ChartValueAxis>
          <ChartValueAxisItem
            axisCrossingValue={minValue}
            background={"transparent"}
            majorGridLines={{
              color: generating ? undefined : "var(--muted-border-color)",
              width: 1,
              skip: 1,
            }}
            minorGridLines={{ visible: false }}
          >
            <ChartCategoryAxisLabels
              color={"var(--color)"}
              content={(e) => valueFormatter(e.value)}
            />
          </ChartValueAxisItem>
        </ChartValueAxis>
        <ChartCategoryAxis>
          <ChartCategoryAxisItem
            type={"date"}
            weekStartDay={1}
            baseUnit={lineType === "area" ? "weeks" : "days"}
            pane={"bottom"}
            majorGridLines={{
              color: generating ? undefined : "var(--muted-border-color)",
              width: 1,
            }}
            maxDivisions={maxDivisions ?? 20}
            labels={{
              color: "var(--cloudy-blue)",
              rotation: "auto",
              position: "start",
              content: (e) => moment(e?.value).format(`DD MMM 'YY`),
            }}
          />
        </ChartCategoryAxis>
      </Chart>
      <ToggableChartLegend
        legendProps={legendProps}
        legend={legend}
        onItemPress={onLegendItemClick}
      />
    </div>
  );
};
